import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError } from "../lib/api";
import { useAdminAuth } from "../context/AdminAuth";
import { useRefetchOnFocus } from "../hooks/useRefetchOnFocus";
import { ArrowLeft, BarChart3, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PERIODS = [
  { id: "today", label: "Heute" },
  { id: "week", label: "Diese Woche" },
  { id: "month", label: "Dieser Monat" },
  { id: "year", label: "Dieses Jahr" },
  { id: "custom", label: "Eigener Zeitraum" },
];

function eur(v) {
  return Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export default function AdminServiceStats() {
  const nav = useNavigate();
  const { admin } = useAdminAuth();
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { period };
      if (period === "custom") { params.start = start; params.end = end; }
      const r = await adminApi.get("/admin/service-stats", { params });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [period, start, end]);

  useEffect(() => {
    if (admin === false) nav("/admin");
    if (admin && period !== "custom") load();
  }, [admin, period, load, nav]);
  useRefetchOnFocus(load);

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-[#4A1D24] text-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button data-testid="stats-back-button" onClick={() => nav("/admin")} className="p-2 rounded-lg hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#C59B6C]" /><h1 className="font-serif text-2xl">Dienstleistungsstatistik</h1></div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-4">
          <div className="flex flex-wrap gap-2" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button key={p.id} data-testid={`period-${p.id}`} onClick={() => setPeriod(p.id)}
                className={`text-sm px-3.5 py-1.5 rounded-full border transition-colors ${period === p.id ? "bg-[#4A1D24] text-white border-[#4A1D24]" : "bg-white text-stone-600 border-stone-200 hover:border-[#C59B6C]"}`}>
                {p.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex flex-wrap items-end gap-3 mt-4">
              <div><Label className="text-xs">Von</Label><Input data-testid="custom-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Bis</Label><Input data-testid="custom-end-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" /></div>
              <Button data-testid="apply-custom-button" onClick={load} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white">Anwenden</Button>
            </div>
          )}
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5" data-testid="stats-total-count">
                <div className="inline-flex p-2 rounded-lg bg-stone-100 text-stone-700"><TrendingUp className="h-4 w-4" /></div>
                <p className="text-2xl font-semibold text-stone-900 mt-3">{data.grand_count}</p>
                <p className="text-xs uppercase tracking-widest text-stone-400 mt-0.5">Durchführungen gesamt</p>
              </div>
              <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm p-5" data-testid="stats-total-revenue">
                <div className="inline-flex p-2 rounded-lg bg-amber-100 text-amber-800"><BarChart3 className="h-4 w-4" /></div>
                <p className="text-2xl font-semibold text-stone-900 mt-3">{eur(data.grand_total)}</p>
                <p className="text-xs uppercase tracking-widest text-stone-400 mt-0.5">Umsatz gesamt</p>
              </div>
            </div>

            <div className="rounded-2xl bg-white/90 border border-stone-200/80 shadow-sm overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-stone-100 text-[11px] uppercase tracking-widest text-stone-400 font-semibold">
                <div className="col-span-5">Dienstleistung</div>
                <div className="col-span-2 text-right">Anzahl</div>
                <div className="col-span-3 text-right">Gesamt</div>
                <div className="col-span-2 text-right">Ø Preis</div>
              </div>
              <div className="divide-y divide-stone-100" data-testid="stats-table">
                {data.rows.length === 0 && <p className="p-6 text-sm text-stone-400">Keine Daten im gewählten Zeitraum.</p>}
                {data.rows.map((r, i) => (
                  <div key={i} data-testid={`stats-row-${i}`} className="grid grid-cols-12 gap-2 px-5 py-3 items-center">
                    <div className="col-span-5 min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{r.name}</p>
                      <p className="text-[11px] text-stone-400">{r.category}</p>
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm text-stone-700">{r.count}×</div>
                    <div className="col-span-3 text-right font-mono text-sm font-semibold text-[#4A1D24]">{eur(r.total)}</div>
                    <div className="col-span-2 text-right font-mono text-sm text-stone-500">{eur(r.avg)}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
